import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { env } from '../../config';
import { useTable, useSortBy, CellProps, Column } from 'react-table';
import Table from '@designSystem/Table';
import { PersonFill } from 'react-bootstrap-icons';
import theme from '@theme';
import Badge from '@designSystem/Badges';
import { Active, Res } from '../../images/sidebar';
import Pagination from '@designSystem/Pagination';
import Spinner from '@designSystem/Spinner';
import PageHeader from '../../components/PageHeader/PageHeader';
import InputWrapper from '@designSystem/InputWrapper';
import ReactSelect from 'react-select';
import TableNoData from '../../components/TableNoData';
import MenuComponent from './VerticalMenu';
import { ModalConfirmResiliation } from './ModalConfirmResiliation';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface ServiceData {
  internalReference: string;
  externalReference: string;
  state: string;
  productOrderItem: Array<{
    product: {
      productOffering: {
        id: string;
      };
    };
  }>;
  customer: {
    name: string | null;
    contact: {
      firstName: string;
      lastName: string;
    };
  };
  date_resiliation: string;
}

const allProductsMap = [
  { label: 'Home', value: 'OPERA Home' },
  { label: 'Business', value: 'OPERA Business' },
  { label: 'Office Pro', value: 'OPERA Office Pro' },
  { label: 'Office 100M', value: 'OPERA Office 100M' },
  { label: 'Office 10M', value: 'OPERA Office 10M' },
  { label: 'Business Fidélisation', value: 'OPERA Business Fidelisation' },
  { label: 'Office Basic', value: 'Opera Basic' }
];

const ServicePage = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [services, setServices] = useState<ServiceData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [serviceIdToResil, setServiceIdToResil] = useState<string>();

  const [currentFilterAndSort, setCurrentFilterAndSort] = useState<{
    search: string;
    products: string[];
  }>({
    search: '',
    products: []
  });

  const { t } = useTranslation();

  const getServices = async () => {
    return axios.get(`${env.API_URL}/services/getAll`, {
      params: {
        currentPage,
        limit: 20,
        searchText: currentFilterAndSort.search,
        products: currentFilterAndSort.products.join(',')
      }
    });
  };

  const resilService = async (serviceId: string) => {
    await axios.post(`${env.API_URL}/services/${serviceId}/resiliation`);
  };

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const response = await getServices();
        setServices(response?.data?.results || []);
        setTotalCount(response?.data?.total || 0);
      } catch (error) {
        console.error('Erreur lors de la récupération des services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [currentPage, currentFilterAndSort]);

  const updateDataFromSearchInput = (newFilter: any) => {
    setCurrentPage(0);
    setCurrentFilterAndSort({ ...currentFilterAndSort, ...newFilter });
  };

  const handleResiliation = async (serviceId: string) => {
    try {
      await resilService(serviceId);
      const response = await getServices();
      setServices(response?.data?.results || []);
      setTotalCount(response?.data?.total || 0);
      toast.success(t('service.resiliation.success', 'Service résilié avec succès'));
    } catch (error) {
      toast.error(t('service.resiliation.error', 'Erreur lors de la résiliation du service'));
    } finally {
      setServiceIdToResil(undefined);
    }
  };

  const columns: Column<ServiceData>[] = useMemo(
    () => [
      {
        Header: 'Identifiant Axione',

        accessor: 'internalReference',
        sortType: 'basic'
      },
      {
        Header: 'Client Final',
        accessor: (row: ServiceData) =>
          row.customer.name || `${row.customer.contact.lastName} ${row.customer.contact.firstName}`,
        id: 'client_final',
        Cell: ({ row }: CellProps<ServiceData>) => {
          const { customer } = row.original;
          return (
            <div className="client-text">
              <Badge
                className={customer.name ? '' : 'text-faded'}
                variant="grey"
                {...{
                  leftContent: (
                    <PersonFill
                      size="10px"
                      color={theme.palette.grey[300]}
                      className="client-icon"
                    />
                  ),
                  children: (
                    <div style={{ fontSize: '10px' }}>
                      {customer.name ||
                        `${customer.contact.lastName} ${customer.contact.firstName}`}
                    </div>
                  )
                }}
              />
            </div>
          );
        }
      },
      {
        Header: 'Statut',
        accessor: 'state',
        Cell: ({ value }: { value: string }) => {
          if (value === 'terminated') {
            return (
              <Badge
                style={{ width: 'fit-content' }}
                variant="red"
                {...{
                  leftContent: <img style={{ marginLeft: '2px' }} src={Res} alt="icon" />
                }}
              >
                Résilié
              </Badge>
            );
          }
          return (
            <Badge
              style={{ width: 'fit-content' }}
              variant="green"
              {...{
                leftContent: <img style={{ marginLeft: '5px' }} src={Active} alt="icon" />
              }}
            >
              <div style={{ marginLeft: '2px' }}>Actif</div>
            </Badge>
          );
        }
      },
      {
        Header: 'Référence Client',

        accessor: 'externalReference'
      },
      {
        Header: 'Produit',
        accessor: (row: ServiceData) => row.productOrderItem[0]?.product.productOffering.id
      },
      {
        Header: 'Action',
        Cell: ({ row }: CellProps<ServiceData>) => {
          if (row.original.productOrderItem[0]?.product.productOffering.id.includes('Business')) {
            return <></>;
          }
          return (
            <MenuComponent
              resil={row.original.date_resiliation}
              onResiliation={() => setServiceIdToResil(row.original.internalReference)}
              isResiliable={row.original.state !== 'terminated'}
            />
          );
        },
        accessor: (row: ServiceData) => row.date_resiliation
      }
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: services,
      initialState: { sortBy: [{ id: 'internalReference', desc: false }] }
    },
    useSortBy
  );

  return (
    <div>
      <PageHeader
        updateData={updateDataFromSearchInput}
        currentFilterAndSort={currentFilterAndSort}
        allowZeroResults
        inputPlaceholder={'Search'}
        totalRows={totalCount}
        totalRowsTitle={'services'}
        rightContent={
          <>
            <InputWrapper>
              <ReactSelect
                isMulti
                name="products"
                options={allProductsMap}
                value={allProductsMap.filter(
                  (option) =>
                    currentFilterAndSort.products.length > 0 &&
                    currentFilterAndSort.products.includes(option.value)
                )}
                onChange={(selectedOptions: any) => {
                  const products = selectedOptions.map((option: any) => option.value);
                  setCurrentFilterAndSort({ ...currentFilterAndSort, products });
                }}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder={'Selectionnez des produits'}
                noOptionsMessage={() => 'Plus de produits'}
              />
            </InputWrapper>
          </>
        }
      />
      <Table {...getTableProps()} maxCols={headerGroups[0]?.headers.length}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={`header_${i}`}>
              {headerGroup.headers.map((column, i) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={`column_${i}`}
                  style={{
                    width: column.id === 'action' ? '80px' : 'auto',
                    minWidth: column.id === 'action' ? '80px' : 'auto',
                    maxWidth: column.id === 'action' ? '80px' : 'auto',
                    cursor: column.canSort ? 'pointer' : 'auto'
                  }}
                >
                  <div style={{ display: 'flex', gap: '5px' }}>
                    {column.render('Header')}
                    <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ' ⇅'}</span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {!loading && rows.length > 0 && (
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={`row${i}`}>
                  {row.cells.map((cell, i) => (
                    <td {...cell.getCellProps()} key={`cell_${i}`}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        )}
      </Table>
      {loading && (
        <div
          style={{
            width: '100%',
            height: '100px',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex'
          }}
        >
          <Spinner spinnerSize={2} />
        </div>
      )}
      {!loading && rows.length === 0 && (
        <TableNoData
          containerStyle={{
            padding: '1rem',
            backgroundColor: 'white',
            borderStyle: 'solid',
            borderColor: ` ${theme.palette.grey[200]}`,
            borderWidth: '0px 1px 1px 1px',
            borderRadius: '0px 0px 8px 8px'
          }}
          title={'No data'}
          description={'No services is found'}
        />
      )}

      <Pagination
        totalRows={totalCount}
        pageChangeHandler={setCurrentPage}
        parentCurrentPage={currentPage}
        rowsPerPage={20}
      />

      <ModalConfirmResiliation
        show={!!serviceIdToResil}
        onClose={() => setServiceIdToResil(undefined)}
        onConfirmResiliation={() => serviceIdToResil && handleResiliation(serviceIdToResil)}
      />
    </div>
  );
};

export default ServicePage;
